import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { epochToUtcDateString } from '@core/lib/utils/model-utils/date-utils';

/**
 * R32 (fix-parent-portal-enrollment-review 3.5): the Application's date field —
 * a Material calendar picker instead of the browser's dd/mm/yyyy text box.
 *
 * The value is the Application's date-only convention: a `yyyy-mm-dd` string
 * (UTC midnight epoch on the way to the server, section-contract's `epoch`
 * fields). The picker works in local calendar days, so the string is decoded
 * and encoded with local parts — never `Date.parse`, which would shift a
 * Toronto parent's day.
 *
 * Extra <mat-error> / <mat-hint> markup projects into the field.
 */
@Component({
  selector: 'ui-date-input',
  imports: [MatFormFieldModule, MatInputModule, MatDatepickerModule],
  providers: [
    provideNativeDateAdapter(),
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => UiDateInputComponent), multi: true },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <mat-form-field appearance="outline" class="date-field" subscriptSizing="dynamic">
      <mat-label>{{ label() }}</mat-label>
      <input
        matInput
        [matDatepicker]="picker"
        [value]="value()"
        [disabled]="isDisabled()"
        [required]="required()"
        (dateChange)="onPick($event.value)"
        (blur)="onTouched()"
        [attr.data-testid]="testId()"
      />
      <mat-datepicker-toggle matIconSuffix [for]="picker" />
      <mat-datepicker #picker />
      <ng-content />
    </mat-form-field>
  `,
  styles: [
    `
      .date-field {
        width: 100%;
      }
    `,
  ],
})
export class UiDateInputComponent implements ControlValueAccessor {
  readonly label = input.required<string>();
  readonly testId = input('date-input');
  readonly required = input(false);

  readonly value = signal<Date | null>(null);
  readonly isDisabled = signal(false);

  private onChange: (v: string | null) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(v: string | null | undefined): void {
    this.value.set(toDate(v ?? null));
  }

  registerOnChange(fn: (v: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.isDisabled.set(disabled);
  }

  onPick(d: Date | null): void {
    this.value.set(d);
    this.onChange(toDateOnly(d));
  }
}

/** `yyyy-mm-dd` (or an epoch in seconds) → the local calendar day it names. */
export function toDate(v: string | number | null): Date | null {
  if (v === null || v === '') return null;
  if (typeof v === 'number') {
    // A date-only epoch is a UTC day: read its UTC parts (local parts show Toronto the day before).
    return v > 0 ? toDate(epochToUtcDateString(v)) : null;
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

/** A picked day → `yyyy-mm-dd` from its local parts (null when cleared). */
export function toDateOnly(d: Date | null): string | null {
  if (!d || Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
